import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const SupportDeskLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Support Desk | Explanatum';
    const desc = 'Enterprise-grade support platform with AI-powered responses, multi-channel inbox, SLA tracking, and real-time collaboration at 1/3 the cost of Zendesk.';
    const url = 'https://explanatum.co.uk/modules/support-desk';
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', url);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section className="relative overflow-hidden" role="region" aria-label="Support Desk overview">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                AI Support Desk
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Enterprise support at startup pricing.
                <span className="mt-2 block text-gray-900">
                  AI-powered responses, unlimited agents, zero seat fees.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Multi-channel inbox, AI response suggestions, SLA tracking, and real-time collaboration. Replace Zendesk, Intercom, or Freshdesk with a platform that learns from your business consultant and costs 70% less.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_support_desk_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start free trial
                </a>
                <a
                  href="#compare"
                  className="inline-flex items-center justify-center rounded-full border border-slate-500/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  See vs Zendesk/Intercom
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Unlimited agents & tickets
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  AI response generation
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Multi-channel inbox
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Support Desk features">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl opacity-40" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl opacity-30" aria-hidden="true" />
              <nav className="relative rounded-3xl border border-gray-200 bg-white shadow-2xl backdrop-blur" aria-labelledby="support-toc-title">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 id="support-toc-title" className="text-lg font-semibold text-gray-900">Complete support platform</h2>
                  <p className="text-sm text-gray-600 mt-1">Everything you need to deliver world-class customer support.</p>
                </div>
                <ol className="p-6 space-y-2 list-decimal list-inside text-gray-900">
                  <li className="text-gray-900">Multi-channel inbox (email, widget, API)</li>
                  <li className="text-gray-900">AI response suggestions & auto-categorization</li>
                  <li className="text-gray-900">SLA tracking & automatic escalation</li>
                  <li className="text-gray-900">Knowledge base with 15 languages</li>
                  <li className="text-gray-900">Real-time collision detection</li>
                  <li className="text-gray-900">Custom fields & workflow automation</li>
                  <li className="text-gray-900">Time tracking & agent performance analytics</li>
                  <li className="text-gray-900">Customer self-service portal</li>
                </ol>
              </nav>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Support Desk benefits" className="bg-gradient-to-b from-white to-slate-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Everything Zendesk has, plus AI superpowers
            </h2>
            <p className="mt-4 text-base text-gray-500">Replace enterprise support tools with a platform that's smarter, faster, and costs 70% less.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              icon: '🤖',
              title: 'AI-Powered Responses',
              body: 'Generate professional replies using conversation context, KB articles, and your business consultant. Sentiment analysis detects frustrated customers automatically.'
            }, {
              icon: '📨',
              title: 'Multi-Channel Inbox',
              body: 'Email-to-ticket parsing, embeddable widget, and API access. All channels unified in one dashboard with automatic threading and collision detection.'
            }, {
              icon: '⏱️',
              title: 'SLA Tracking & Escalation',
              body: 'Set response and resolution targets with business hours support. Automatic escalation on SLA breaches with smart agent assignment rules.'
            }, {
              icon: '📚',
              title: 'Knowledge Base 2.0',
              body: '15 languages, full-text search, analytics, helpfulness ratings, and AI article suggestions. Public portal with custom branding.'
            }, {
              icon: '👥',
              title: 'Real-Time Collaboration',
              body: 'See who\'s viewing tickets, typing indicators, and automatic locks prevent duplicate work. Macros and canned responses for speed.'
            }, {
              icon: '🎯',
              title: 'Smart Automation',
              body: 'Visual workflow builder with if/then logic, auto-assignment based on keywords/priority, round-robin with load balancing.'
            }, {
              icon: '⚙️',
              title: 'Custom Fields & Forms',
              body: '9 field types (text, select, date, etc.) with validation rules. Capture business-specific data and filter tickets your way.'
            }, {
              icon: '⏲️',
              title: 'Time Tracking & Billing',
              body: 'Track agent time per ticket with billable/non-billable hours. Calculate costs automatically for client billing or productivity analysis.'
            }, {
              icon: '📊',
              title: 'Enterprise Analytics',
              body: 'Agent performance dashboards, SLA compliance reports, CSAT surveys, and real-time metrics. Export data via API.'
            }, {
              icon: '🔄',
              title: 'Ticket Merge & Split',
              body: 'Combine duplicates or split complex issues with full audit trails. Messages and custom fields migrate automatically.'
            }, {
              icon: '🌐',
              title: 'Customer Portal',
              body: 'Self-service portal with custom branding, ticket viewing, KB search, and email authentication. White-label ready.'
            }, {
              icon: '🧠',
              title: 'Business Consultant Integration',
              body: 'Share tickets to AI consultant for strategic insights. Context-aware across your entire business platform—no competitor has this.'
            }].map((item, idx) => (
              <div key={idx} className="relative rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Platform comparison" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              How we compare to market leaders
            </h2>
            <p className="mt-4 text-base text-gray-500">Same features, better AI, fraction of the cost</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-900">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-blue-600">Explanatum</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-500">Zendesk</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-500">Intercom</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-500">Freshdesk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { feature: 'Multi-channel inbox', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'AI response generation', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'SLA tracking & escalation', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'Collision detection', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'Custom fields', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'Workflow automation', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'Time tracking & billing', explanatum: true, zendesk: true, intercom: false, freshdesk: true },
                  { feature: 'Multi-language KB (15)', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'Customer portal', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'Real-time dashboards', explanatum: true, zendesk: true, intercom: true, freshdesk: true },
                  { feature: 'Business Consultant AI', explanatum: true, zendesk: false, intercom: false, freshdesk: false },
                  { feature: 'Unlimited agents', explanatum: true, zendesk: false, intercom: false, freshdesk: false },
                  { feature: 'Self-hosted option', explanatum: true, zendesk: false, intercom: false, freshdesk: false },
                  { feature: 'Monthly cost (25 agents)', explanatum: '£19-49', zendesk: '£1,225+', intercom: '£1,850+', freshdesk: '£1,050+' }
                ].map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">{row.feature}</td>
                    <td className="py-3 px-4 text-center text-sm">
                      {typeof row.explanatum === 'boolean' ? (
                        row.explanatum ? <span className="text-2xl">✅</span> : <span className="text-2xl">❌</span>
                      ) : (
                        <span className="font-bold text-green-600">{row.explanatum}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {typeof row.zendesk === 'boolean' ? (
                        row.zendesk ? <span className="text-2xl">✅</span> : <span className="text-2xl">❌</span>
                      ) : (
                        <span className="text-gray-600">{row.zendesk}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {typeof row.intercom === 'boolean' ? (
                        row.intercom ? <span className="text-2xl">✅</span> : <span className="text-2xl">❌</span>
                      ) : (
                        <span className="text-gray-600">{row.intercom}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {typeof row.freshdesk === 'boolean' ? (
                        row.freshdesk ? <span className="text-2xl">✅</span> : <span className="text-2xl">❌</span>
                      ) : (
                        <span className="text-gray-600">{row.freshdesk}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-8 text-center">
            <p className="text-lg font-semibold text-gray-900">
              💰 Save £26K-102K over 5 years compared to Zendesk
            </p>
            <p className="text-sm text-gray-500 mt-2">Based on 25 agents. Unlimited users included at no extra cost.</p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-gradient-to-b from-slate-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-base text-gray-500">All features included. No hidden fees. No per-agent pricing.</p>
          </div>
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default SupportDeskLanding;
